import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

export type DialogTone = 'primary' | 'danger';

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: DialogTone;
  /** A primeicons class name, e.g. `pi-trash`. Defaults to one matching the tone. */
  icon?: string;
}

export interface AlertOptions {
  title: string;
  body?: ReactNode;
  closeLabel?: string;
  tone?: DialogTone;
  icon?: string;
}

interface DialogApi {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  alert: (options: AlertOptions) => Promise<void>;
}

interface PendingDialog {
  kind: 'confirm' | 'alert';
  options: ConfirmOptions & AlertOptions;
  resolve: (confirmed: boolean) => void;
}

const DialogContext = createContext<DialogApi | null>(null);

/**
 * The application's one modal, so the browser's own `alert` and `confirm` - which cannot be styled, cannot be
 * translated and block the whole tab - are never used. Bootstrap's markup driven from React rather than from
 * Bootstrap's JavaScript, which also means the tests never wait for a CSS transition.
 */
export function DialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingDialog | null>(null);

  const api = useMemo<DialogApi>(
    () => ({
      confirm: (options) =>
        new Promise<boolean>((resolve) => {
          setPending({ kind: 'confirm', options, resolve });
        }),
      alert: (options) =>
        new Promise<void>((resolve) => {
          setPending({ kind: 'alert', options, resolve: () => resolve() });
        }),
    }),
    [],
  );

  const settle = useCallback(
    (confirmed: boolean) => {
      setPending((current) => {
        current?.resolve(confirmed);
        return null;
      });
    },
    [],
  );

  return (
    <DialogContext.Provider value={api}>
      {children}
      {pending && <DialogSurface dialog={pending} onSettle={settle} />}
    </DialogContext.Provider>
  );
}

export function useDialog(): DialogApi {
  const api = useContext(DialogContext);
  if (api === null) {
    throw new Error('useDialog must be used inside a <DialogProvider>.');
  }

  return api;
}

const DEFAULT_ICON: Record<DialogTone, string> = {
  primary: 'pi-info-circle',
  danger: 'pi-exclamation-triangle',
};

function DialogSurface({
  dialog,
  onSettle,
}: {
  dialog: PendingDialog;
  onSettle: (confirmed: boolean) => void;
}) {
  const { t } = useTranslation();
  const confirmButton = useRef<HTMLButtonElement>(null);
  const tone = dialog.options.tone ?? (dialog.kind === 'confirm' ? 'danger' : 'primary');
  const icon = dialog.options.icon ?? DEFAULT_ICON[tone];

  // Opening a dialog moves the keyboard there, and Escape answers it the same way Cancel does.
  useEffect(() => {
    confirmButton.current?.focus({ preventScroll: true });

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onSettle(false);
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onSettle]);

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div
        className="modal fade show d-block"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        // Clicking the sheet itself must not answer the question, only the area around it.
        onClick={(event) => event.target === event.currentTarget && onSettle(false)}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content app-dialog">
            <div className="modal-header">
              <h2 className="modal-title h5 d-flex align-items-center gap-2" id="app-dialog-title">
                <i className={`pi ${icon} app-dialog-icon ${tone}`} aria-hidden="true" />
                {dialog.options.title}
              </h2>
              <button
                type="button"
                className="btn-close"
                aria-label={t('common.close')}
                onClick={() => onSettle(false)}
              />
            </div>

            {dialog.options.body !== undefined && (
              <div className="modal-body">{dialog.options.body}</div>
            )}

            <div className="modal-footer">
              {dialog.kind === 'confirm' && (
                <button type="button" className="btn btn-light" onClick={() => onSettle(false)}>
                  {dialog.options.cancelLabel ?? t('common.cancel')}
                </button>
              )}
              <button
                ref={confirmButton}
                type="button"
                className={`btn btn-${tone}`}
                onClick={() => onSettle(true)}
              >
                {dialog.kind === 'confirm'
                  ? dialog.options.confirmLabel ?? t('common.confirm')
                  : dialog.options.closeLabel ?? t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
